import cookie from 'js-cookie';
import { clone, each, find, get, isEmpty } from 'lodash';
import fetchBookingState, {
    requiresRedirect,
} from '../../../ssr/Booking/data/fetchBookingState-shared';
import { updateAddressFailure } from '../../components/AppointmentRequirements/components/AddressForm/AddressFormActions';
import { usingInsuranceError } from '../../components/AppointmentRequirements/components/UsingInsuranceConfirmation/usingInsuranceError';
import {
    warningMapping,
    OBGYN_WARNING,
    ADDRESS_CONFIRMATION,
    AGE_WARNING,
    PRIMARY_CARE_WARNING,
    REFERRAL_REQUIRED,
    USING_INSURANCE_WARNING,
    USING_INSURANCE_WARNING_ERROR_STRING,
} from '../../components/AppointmentRequirements/constants';
import { hasRequirement } from '../../components/AppointmentRequirements/helpers';
import {
    BookingStateProperties,
    HttpStatusCodes,
    REVIEW_AND_BOOK_ROOT_KEY,
} from '../../constants';
import bookingStateSelector from '../../selectors/BookingStateSelector';
import {
    stringifyIdsFromBody,
    destringifyIdsForBody,
} from '../../utils/bodyIdTransform';
import { redirectOnErrorResponse } from '../../utils/redirectOnErrorResponse';
import { ServerException } from '../constants';
import { errorCodes, SchedulingActions } from './constants';
import {
    resetAsyncState,
    updatingBookingState,
    updateLocalBookingState,
    updateBookingStateSuccess,
    updateBookingStateFailure,
    gettingBookingState,
    getBookingStateSuccess,
    getBookingStateFailure,
    submittingBooking,
    submitBookingSuccess,
    submitBookingFailure,
} from './SynchronousSchedulingActions';

const apiHost = process.env.API_HOST || '';
const schedulingBookingStateEndpoint = (bookingId) =>
    `${apiHost}/api/rest/scheduling/v1/requests/${bookingId}`;
const submitBookingEndpoint = (bookingId) =>
    `${apiHost}/api/rest/scheduling/v1/requests/${bookingId}/book`;
const addressEndpoint = (patientId) =>
    `${apiHost}/api/web/2/patient/${patientId}/address`;

export function getBookingStateFetch(bookingId) {
    return fetchBookingState({
        fetchFunc: (url) =>
            fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                },
                credentials: 'include',
            }),
        bookingId,
    });
}

let isFetching = false;
export function getBookingState(bookingId, forceFetch) {
    return (dispatch, getState) => {
        const bookingState = bookingStateSelector(getState());

        if (isFetching) {
            return;
        }

        isFetching = true;
        dispatch(gettingBookingState());

        if (!isEmpty(bookingState) && !forceFetch) {
            dispatch(getBookingStateSuccess(bookingState));
            return;
        }

        return getBookingStateFetch(bookingId)
            .then(({ status, body }) => {
                if (status === HttpStatusCodes.OK && body) {
                    dispatch(getBookingStateSuccess(body));
                    isFetching = false;
                } else {
                    throw new ServerException({ response: { status } });
                }
            })
            .catch((exception) => {
                isFetching = false;
                dispatch(getBookingStateFailure());

                if (requiresRedirect(get(exception, 'response.status'))) {
                    redirectOnErrorResponse(dispatch, {
                        bookingId,
                        responseStatus: exception.response.status,
                    });
                }
            });
    };
}

export function updateBookingStateFetch(dispatch, bookingState) {
    const updateBookingStateFetchOptions = {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        [BookingStateProperties.bookingId]:
            bookingState[BookingStateProperties.bookingId],
        body: JSON.stringify(destringifyIdsForBody(bookingState)),
        credentials: 'include',
    };

    dispatch(updateLocalBookingState(bookingState));
    dispatch(updatingBookingState());

    return fetch(
        schedulingBookingStateEndpoint(bookingState.id),
        updateBookingStateFetchOptions
    ).then((response) => {
        if (response.status === HttpStatusCodes.UnprocessableEntity) {
            throw new ServerException({
                errorCode: errorCodes.UPDATE_ERROR,
                response,
                bookingState,
            });
        } else if (requiresRedirect(response.status)) {
            throw new ServerException({
                response,
                errorCode: errorCodes.UPDATE_ERROR,
            });
        }

        return response
            .json()
            .then(stringifyIdsFromBody)
            .then((body) => {
                if (response.status === HttpStatusCodes.OK && body) {
                    dispatch(updateBookingStateSuccess(body));
                } else if (response.status === HttpStatusCodes.OK) {
                    throw new ServerException({
                        errorCode: errorCodes.UPDATE_ERROR,
                    });
                } else {
                    throw new ServerException({
                        errorCode: errorCodes.UPDATE_ERROR,
                        errorFields: body.error.fields,
                    });
                }
            });
    });
}

function handleErrorInUpdateBookingState(dispatch, bookingState) {
    return (exception) => {
        if (requiresRedirect(get(exception, 'response.status'))) {
            dispatch(updateBookingStateFailure());
            return redirectOnErrorResponse(dispatch, {
                bookingState,
                responseStatus: exception.response.status,
            });
        } else if (exception.errorFields) {
            return dispatch(updateBookingStateFailure(exception.errorFields));
        } else {
            return dispatch(updateBookingStateFailure());
        }
    };
}

export function updateBookingState(bookingState) {
    return (dispatch) => {
        return updateBookingStateFetch(dispatch, bookingState).catch(
            handleErrorInUpdateBookingState(dispatch, bookingState)
        );
    };
}

export function updateExistingPatientState(isExistingPatient) {
    return (dispatch, getState) => {
        const bookingState = bookingStateSelector(getState());
        const updatedBookingState = {
            ...bookingState,
            [BookingStateProperties.existingPatient]: isExistingPatient,
        };

        dispatch({
            type: SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE,
            payload: { isExistingPatient },
        });

        return updateBookingStateFetch(dispatch, updatedBookingState)
            .then(() =>
                dispatch({
                    type: SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE_SUCCESS,
                    payload: { isExistingPatient },
                })
            )
            .catch((exception) => {
                dispatch({
                    type: SchedulingActions.UPDATE_IS_EXISTING_PATIENT_STATE_FAILURE,
                });
                return handleErrorInUpdateBookingState(
                    dispatch,
                    bookingState
                )(exception);
            });
    };
}

function getRequirementByKey(bookingState, requirement) {
    return find(bookingState.requirements, (req) => req.key === requirement);
}

function getRequirementValue(requirementsStore, requirement) {
    const storeFieldName = warningMapping[requirement];
    return requirementsStore[storeFieldName].value;
}

const requirementsToCheck = [
    OBGYN_WARNING,
    PRIMARY_CARE_WARNING,
    AGE_WARNING,
    REFERRAL_REQUIRED,
    ADDRESS_CONFIRMATION,
];

function mergeBookingStateWithRequirementsState(
    bookingState,
    requirementsStore
) {
    const mergedBookingState = clone(bookingState);

    each(requirementsToCheck, (requirementToCheck) => {
        const bookingRequirement = getRequirementByKey(
            mergedBookingState,
            requirementToCheck
        );
        if (bookingRequirement) {
            if (requirementToCheck === ADDRESS_CONFIRMATION) {
                bookingRequirement.value = true;
            } else {
                bookingRequirement.value = getRequirementValue(
                    requirementsStore,
                    requirementToCheck
                );
            }
        }
    });

    return mergedBookingState;
}

export function updateAddressFetch({ patientId, addressObj = {} }) {
    const updateAddressFetchOptions = {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            address_line_1: addressObj.address1,
            address_line_2: addressObj.address2,
            zip_code: `${addressObj.zip}`.trim(),
            state: addressObj.selectedState,
            city: addressObj.city,
        }),
        credentials: 'include',
    };

    return fetch(addressEndpoint(patientId), updateAddressFetchOptions).then(
        (response) => {
            if (response.status !== HttpStatusCodes.OK) {
                return response.json().then((body) => {
                    throw new ServerException({
                        errorCode: errorCodes.ADDRESS_ERROR,
                        errorFields: get(body, 'error.fields'),
                    });
                });
            }
        }
    );
}

export function submitBookingFetch(
    dispatch,
    {
        bookingId,
        appointmentNotes = '',
        memberId = '',
        mentalHealthQuestionnaire = null,
    },
    afterSubmitting
) {
    let body = {
        appointment_notes: appointmentNotes,
        member_id: memberId,
    };
    if (mentalHealthQuestionnaire) {
        body = {
            ...body,
            mental_health_questionnaire_answer: mentalHealthQuestionnaire,
        };
    }

    // get one time booking link id from cookies
    const otblId = cookie.get('otbl_id');
    if (otblId) {
        body = {
            ...body,
            one_time_booking_guid: otblId,
        };
    }

    const submitBookingFetchOptions = {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
    };

    dispatch(submittingBooking());

    if (afterSubmitting) {
        dispatch(afterSubmitting());
    }

    return fetch(
        submitBookingEndpoint(bookingId),
        submitBookingFetchOptions
    ).then((response) => {
        if (requiresRedirect(response.status)) {
            throw new ServerException({ response });
        }

        return response
            .json()
            .then(stringifyIdsFromBody)
            .then((body) => {
                if (body && response.status === HttpStatusCodes.Created) {
                    cookie.remove('otbl_id');
                    dispatch(submitBookingSuccess(body));
                } else {
                    throw new ServerException({
                        errorCode: errorCodes.SUBMIT_ERROR,
                        errorFields: get(body, 'error.fields'),
                        type: get(body, 'error.type'),
                        message: get(body, 'error.message'),
                        code: get(body, 'error.code'),
                        response,
                    });
                }
            });
    });
}

export function submitBooking(bookingId, afterSubmitting) {
    return (dispatch, getState) => {
        dispatch(resetAsyncState());

        const state = getState();
        const appointmentNotes = state.appointmentDetailsPage.doctorNotes;
        const mentalHealthQuestionnaire =
            state?.[REVIEW_AND_BOOK_ROOT_KEY]?.mentalHealthQuestionnaire;
        const memberId = state.appointmentDetailsPage.insuranceId;
        const { bookingState } = state.scheduling;

        const requirementsStore = state.appointmentRequirements;

        if (
            hasRequirement(
                USING_INSURANCE_WARNING,
                bookingState.requirements
            ) &&
            !requirementsStore.usingInsuranceConfirmation.value
        ) {
            return dispatch(
                usingInsuranceError(USING_INSURANCE_WARNING_ERROR_STRING)
            );
        }

        return new Promise((resolve) => {
            if (
                hasRequirement(ADDRESS_CONFIRMATION, bookingState.requirements)
            ) {
                const addressObj = requirementsStore.addressform;
                const patientId =
                    bookingState[BookingStateProperties.patientId];

                resolve(
                    updateAddressFetch({
                        patientId,
                        addressObj,
                    })
                );
            } else {
                resolve();
            }
        })
            .then(() =>
                mergeBookingStateWithRequirementsState(
                    bookingState,
                    requirementsStore
                )
            )
            .then((mergedBookingState) =>
                updateBookingStateFetch(dispatch, mergedBookingState)
            )
            .then(() =>
                submitBookingFetch(
                    dispatch,
                    {
                        bookingId,
                        appointmentNotes,
                        memberId,
                        mentalHealthQuestionnaire,
                    },
                    afterSubmitting
                )
            )
            .catch((exception) => {
                if (requiresRedirect(get(exception, 'response.status'))) {
                    if (exception.errorCode === errorCodes.UPDATE_ERROR) {
                        dispatch(
                            updateBookingStateFailure(exception.errorFields)
                        );
                        dispatch(submitBookingFailure());
                    }

                    redirectOnErrorResponse(dispatch, {
                        bookingState,
                        responseStatus: exception.response.status,
                    });
                } else if (exception.errorCode === errorCodes.ADDRESS_ERROR) {
                    dispatch(updateAddressFailure(exception.errorFields));
                    dispatch(submitBookingFailure());
                } else if (exception.errorCode === errorCodes.SUBMIT_ERROR) {
                    if (
                        exception.code === 'GENERAL' &&
                        isUsingInsuranceError(exception)
                    ) {
                        const { message } =
                            usingInsuranceErrorField(exception) || {};
                        dispatch(usingInsuranceError(message));
                    }

                    let bookingErrors = [];
                    if (exception.errorFields) {
                        bookingErrors = exception.errorFields;
                    } else if (exception.message) {
                        bookingErrors = [
                            {
                                message: exception.message,
                            },
                        ];
                    }

                    dispatch(
                        submitBookingFailure({
                            bookingErrors,
                            bookingErrorCode: exception.code,
                        })
                    );
                } else if (exception.errorCode === errorCodes.UPDATE_ERROR) {
                    if (exception.errorCode === errorCodes.UPDATE_ERROR) {
                        dispatch(
                            updateBookingStateFailure({
                                bookingErrors: exception.errorFields,
                            })
                        );
                        dispatch(
                            submitBookingFailure({
                                bookingErrors: exception.errorFields,
                            })
                        );
                    }
                } else {
                    dispatch(submitBookingFailure());
                    throw exception;
                }
            }); // return new promise
    };
}

function usingInsuranceErrorField(exception) {
    const fields = (exception && exception.errorFields) || [];
    return find(
        fields,
        (f = {}) => f.name === 'requirements.using_insurance_warning'
    );
}

function isUsingInsuranceError(exception) {
    return !!usingInsuranceError(exception);
}
